import type { Dayjs } from 'dayjs';
import { isNotNullOrUndefined, isNullOrUndefined, vyDate, withDefault } from '@vy/utils';
import { PRICING_MODELS } from '@vy/types';
import { useFormContext, Controller, type ControllerRenderProps, type FieldValues } from 'react-hook-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import { resetInstallmentsForStartDate } from './reset-installments';
import { getContractEndDate } from '../../../create-plan-page-services/get-contract-end-date';
import { ZERO_DURATION_MONTHS } from '../../../create-plan-page-consts/create-plan-default-values';
import { VyDatePicker } from '@vy/ui-design-system';

type FixedChargeDatePickerProps = {
  productIndex: number;
  variant: 'start' | 'end';
};

const labelDescriptionMap: Record<FixedChargeDatePickerProps['variant'], string> = {
  start: 'Start billing this product on a different date than the contract start date',
  end: 'Stop billing this product before the contract end date',
};

export const FixedChargeDatePicker: React.FC<FixedChargeDatePickerProps> = ({ productIndex, variant }) => {
  const { register, control, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const productEndDateRegister = register(`fixedProducts.${productIndex}.endDate`);
  const productStartDateRegister = register(`fixedProducts.${productIndex}.startDate`);

  const pricingModel = getValues(`fixedProducts.${productIndex}.variants.0.pricing.pricingModel`);
  const contractStartDate = getValues('startDate');
  const contractDurationInMonths = getValues('billingData.duration');
  const contractDurationOrZeroMonths = withDefault(contractDurationInMonths, ZERO_DURATION_MONTHS);
  const contractEndDate = getContractEndDate(
    vyDate(contractStartDate).toISOString(),
    contractDurationOrZeroMonths,
  );
  const amount = getValues(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.price`);

  const isCommercialTerms = pricingModel === PRICING_MODELS.CommercialTerms;
  const registerToUse = variant === 'start' ? productStartDateRegister : productEndDateRegister;
  const labelDescription = labelDescriptionMap[variant];

  const handleDateChange = (field: ControllerRenderProps<FieldValues, string>) => {
    return (newDate: Dayjs | null) => {
      if (isNullOrUndefined(newDate)) {
        field.onChange(null);
        return;
      }

      const day = vyDate(newDate.toDate(), { keepLocalTime: true });
      const dateToSet = (variant === 'start' ? day.startOf('day') : day.endOf('day')).toDate();
      field.onChange(dateToSet);

      if (!isCommercialTerms) {
        return;
      }

      resetInstallmentsForStartDate({ productIndex, newStartDate: dateToSet, amount, getValues, setValue });
    };
  };

  return (
    <Controller
      control={control}
      name={registerToUse.name}
      render={({ field }) => (
        <VyDatePicker
          label={`Custom ${variant} date`}
          helperText={labelDescription}
          clearable
          value={isNotNullOrUndefined(field.value) ? vyDate(field.value) : null}
          onChange={handleDateChange(field)}
          shouldDisableDate={(date: Dayjs) => {
            const contractStart = vyDate(contractStartDate);
            const currentDate = vyDate(date.toDate());
            const isBeforeContractStart = currentDate.isBefore(contractStart, 'day');
            const isAfterContractEnd = currentDate.isAfter(contractEndDate, 'day');
            return isBeforeContractStart || isAfterContractEnd;
          }}
          data-vy="plan-charge-drawer-custom-date-picker"
        />
      )}
    />
  );
};
